import { useEffect, useMemo, useRef, useState } from 'react';

import { uploadGroupFile } from '@src/api/file';
import CustomTag from '@src/components/Tag';
import useGetMembers from '@src/hooks/queries/member/useGetMembers';
import { openModal } from '@src/reduce/modals';
import getIsResponseFalse from '@src/utils/getIsResponseFalse';
import { Button, DatePicker, Input, Select, message } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';

import { ReactComponent as DateIcon } from '@assets/icons/date-suffix.svg';
import { ReactComponent as MapIcon } from '@assets/icons/map.svg';
import { ReactComponent as PlusIcon } from '@assets/icons/plus.svg';
import { ReactComponent as SelectIcon } from '@assets/icons/select-suffix.svg';
import { ReactComponent as UploadIcon } from '@assets/icons/upload.svg';

import { FormProps } from '../type';
import * as S from './styled';

const actionTypeOptions = [
  { value: 'homepage', label: '홈페이지 게시' },
  { value: 'band', label: '밴드 게시' },
  { value: 'mail', label: '메일 발송' },
  { value: 'sms', label: '문자 발송' },
  { value: 'funeralFlower', label: '조화' },
  { value: 'article', label: '장례용품' },
];

const targetOptions = [
  { value: '본인상', label: '본인상' },
  { value: '부친상', label: '부친상' },
  { value: '모친상', label: '모친상' },
  { value: '시부상', label: '시부상' },
  { value: '시모상', label: '시모상' },
  { value: '빙부상', label: '빙부상' },
  { value: '빙모상', label: '빙모상' },
  { value: '배우자상', label: '배우자상' },
  { value: '자녀상', label: '자녀상' },
  { value: 'etc', label: '직접 입력' },
];

const remarksOptions = [
  { value: 'directInput', label: '직접 입력' },
  {
    value: '황망한 마음에 일일이 연락드리지 못함을 널리 혜량해 주시길 바랍니다.',
    label: '황망한 마음에 일일이 연락드리지 못함을 널리 혜량해 주시길 바랍니다.',
  },
  {
    value: '가시는 길 깊은 애도와 명복을 빌어주시길 진심으로 바랍니다.',
    label: '가시는 길 깊은 애도와 명복을 빌어주시길 진심으로 바랍니다.',
  },
];

const contactFormat = /^.+\(\d{2,3}-\d{3,4}-\d{4}\)$/;

const FormFuneral = ({
  formData,
  setFormData,
  setTitle,
  hasInitialFormData,
  isEditable,
  initialFileName,
  approveStatus,
  licenseNosMember,
}: FormProps) => {
  const {
    licenseNos,
    targetType,
    targetName,
    date,
    funeralDate,
    address,
    detailAddress,
    contacts,
    remarks,
    paymentAccount,
    actionType,
    customTitle,
    link,
  } = formData;

  const dispatch = useDispatch();

  const inputFileRef = useRef<HTMLInputElement>(null);

  const [memberSearchName, setMemberSearchName] = useState<string>();
  const [contact, setContact] = useState<string>('');
  const [fileName, setFileName] = useState<string>(initialFileName ?? '');
  const [isDirectInput, setIsDirectInput] = useState<boolean>(false);
  const [isTargetTypeDirectInput, setIsTargetTypeDirectInput] = useState<boolean>(() =>
    targetOptions.some(({ value }) => value === targetType) || !targetType ? false : true,
  );

  const processedTitle = useMemo(() => {
    if (!memberSearchName) {
      return '';
    }

    let processed = memberSearchName.replaceAll(/[ㄱ-ㅎ]/g, '');

    return processed;
  }, [memberSearchName]);

  const { data } = useGetMembers({
    page: 1,
    limit: processedTitle ? 20 : 100,
    q: processedTitle ? processedTitle : undefined,
    searchable: false,
  });

  const eventName = useMemo(() => {
    const result = `[訃音] ${customTitle || ''} ${targetType}`;

    setTitle(result);

    return result;
  }, [targetType, customTitle]);

  useEffect(() => {
    if (!hasInitialFormData) {
      const userInfo = data
        .concat(licenseNosMember)
        .find(({ licenseNo }) => licenseNos.includes(licenseNo));
      if (!userInfo) return;

      const { name, licenseCb, officeName } = userInfo;
      setFormData(formData => ({
        ...formData,
        customTitle: `${name}(${licenseCb ? licenseCb + '기' : '경력'}${
          officeName ? '/' + officeName : ''
        }) 노무사님`,
      }));
    }
  }, [licenseNos, licenseNosMember]);

  useEffect(() => {
    if (!targetType) return;
    setIsTargetTypeDirectInput(
      targetOptions.some(({ value }) => value === targetType) ? false : true,
    );
  }, [targetType]);

  const isContactValid = useMemo(() => {
    if (contactFormat.test(contact) || !contact) {
      return true;
    }
    return false;
  }, [contact]);

  const options = useMemo(() => {
    const result = data
      .concat(licenseNosMember)
      .map(({ officeName, name, licenseCb, licenseNo }) => ({
        label: `${name}(${licenseCb ? licenseCb + '기' : '경력'}${
          officeName ? '/' + officeName : ''
        })`,
        value: licenseNo,
      }));

    return result;
  }, [data, licenseNosMember]);

  const addContact = () => {
    if (!contact) {
      message.error('연락처를 입력해주세요.');
      return;
    }
    if (!isContactValid) {
      message.error('연락처 형식이 올바르지 않습니다.');
      return;
    }

    const contactInfo = {
      name: contact.split('(')[0],
      phone: contact.split('(')[1].split(')')[0],
    };

    setContact('');
    setFormData({ ...formData, contacts: [...contacts, contactInfo] });
  };

  const uploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputFiles = e.target.files;
    console.log({ e, inputFiles });
    if (!inputFiles?.length) return;

    const formData = new FormData();
    formData.append('file', inputFiles[0]);

    const response = await uploadGroupFile({ data: formData });

    if (getIsResponseFalse(response)) return;

    setFileName(inputFiles[0].name);
    setFormData(prev => ({ ...prev, fileIds: [response] }));
  };

  const disabled = hasInitialFormData && !isEditable;
  const baseInfoDisabled = hasInitialFormData && !(isEditable && approveStatus === 'pending');

  return (
    <S.Container>
      <S.Item>
        <p>
          회원명<span style={{ color: '#FF0000' }}>*</span>
        </p>
        <Select
          className='right_content'
          showSearch
          value={licenseNos[0]}
          options={options}
          filterOption={false}
          disabled={baseInfoDisabled}
          placeholder='회원을 선택해주세요.'
          onSearch={e => setMemberSearchName(e)}
          onChange={e => {
            setFormData({ ...formData, licenseNos: [e] });
          }}
          suffixIcon={false}></Select>
      </S.Item>
      <S.Item>
        <p>
          회원 정보<span style={{ color: '#FF0000' }}>*</span>
        </p>
        <Input
          disabled={baseInfoDisabled}
          className='right_content'
          placeholder='회원 정보를 입력해주세요.'
          value={customTitle}
          onChange={({ target: { value } }) => {
            setFormData({ ...formData, customTitle: value });
          }}
        />
      </S.Item>
      <S.Item>
        <p>
          경조 대상자 선택<span style={{ color: '#FF0000' }}>*</span>
        </p>

        {isTargetTypeDirectInput ? (
          <Input
            disabled={baseInfoDisabled}
            style={{ borderRadius: '16px' }}
            className='right_content'
            value={targetType}
            onChange={({ target: { value } }) => {
              setFormData({ ...formData, targetType: value });
            }}
            suffix={
              <div
                style={{ display: 'flex' }}
                onClick={() => {
                  setIsTargetTypeDirectInput(false);
                  setFormData({ ...formData, targetType: '' });
                }}>
                <SelectIcon />
              </div>
            }
          />
        ) : (
          <Select
            className='right_content'
            disabled={baseInfoDisabled}
            value={targetType || undefined}
            placeholder='경조 대상자를 선택하세요.'
            onChange={e => {
              if (e === 'etc') {
                setIsTargetTypeDirectInput(true);
                setFormData({ ...formData, targetType: '' });
                return;
              }

              setFormData({ ...formData, targetType: e });
            }}
            options={targetOptions}
            suffixIcon={<SelectIcon />}
          />
        )}
      </S.Item>
      <S.Item>
        <p>
          경조명<span style={{ color: '#FF0000' }}>*</span>
        </p>
        <Input className='right_content' disabled value={eventName} />
      </S.Item>
      <S.Item>
        <p>
          고인명<span style={{ color: '#FF0000' }}>*</span>
        </p>
        <Input
          className='right_content'
          placeholder='고인명을 입력하세요.'
          disabled={disabled}
          value={targetName}
          onChange={({ target: { value } }) => {
            setFormData({ ...formData, targetName: value });
          }}
          prefix={'故'}
          style={{ borderRadius: '16px' }}
        />
      </S.Item>
      <S.Items>
        <p>
          장소<span style={{ color: '#FF0000' }}>*</span>
        </p>
        <div className='right_content'>
          <Input
            value={address}
            disabled={disabled}
            placeholder='빈소를 입력하세요.'
            onFocus={() => {
              dispatch(openModal({ name: 'postcodeModal', props: { setFormData } }));
            }}
            suffix={<MapIcon />}
            style={{ borderRadius: '16px', marginBottom: '16px' }}
          />
          <Input
            placeholder='식장명, 호실 등 상세주소를 입력하세요.'
            value={detailAddress}
            disabled={disabled}
            onChange={({ target: { value } }) => {
              setFormData({ ...formData, detailAddress: value });
            }}
          />
        </div>
      </S.Items>
      <S.Item>
        <p>
          별세 일시<span style={{ color: '#FF0000' }}>*</span>
        </p>
        <DatePicker
          disabled={disabled}
          className='right_content'
          format='YYYY-MM-DD'
          value={date ? dayjs(date) : undefined}
          showNow={false}
          placement={'bottomRight'}
          placeholder='별세일을 선택하세요.'
          suffixIcon={<DateIcon />}
          onChange={e => {
            setFormData({ ...formData, date: dayjs(e).format('YYYY-MM-DD') });
          }}
        />
      </S.Item>
      <S.Item>
        <p>발인 일시</p>
        <DatePicker
          disabled={disabled}
          className='right_content'
          format='YYYY-MM-DD HH:mm'
          value={funeralDate ? dayjs(funeralDate) : undefined}
          showTime
          showNow={false}
          placement={'bottomRight'}
          placeholder='발인일을 선택하세요.'
          suffixIcon={<DateIcon />}
          onChange={e => {
            setFormData({ ...formData, funeralDate: dayjs(e).format('YYYY-MM-DD HH:mm') });
          }}
        />
      </S.Item>
      <S.Items
        style={{
          alignItems: isContactValid && contacts.length < 1 ? 'center' : 'unset',
        }}>
        <p>회원 연락처</p>
        <div className='right_content'>
          {contacts.map(({ name, phone }, index) => (
            <Input
              key={index}
              value={`${name}(${phone})`}
              disabled
              style={{ marginBottom: '16px' }}
            />
          ))}
          <div style={{ display: 'flex' }}>
            <Input
              disabled={disabled}
              value={contact}
              onChange={({ target: { value } }) => {
                setContact(value);
              }}
              placeholder='이름(010-1234-1234)'
            />
            <Button
              disabled={disabled}
              style={{ display: 'flex', alignItems: 'center', marginLeft: '16px' }}
              onClick={addContact}>
              <PlusIcon />
              <span style={{ marginLeft: '8px' }}>연락처 추가</span>
            </Button>
          </div>

          {!isContactValid && (
            <div style={{ color: '#F60303', marginTop: '8px' }}>
              '이름(010-1234-1234)' 형식만 입력 가능합니다.
            </div>
          )}
        </div>
      </S.Items>
      <S.Items style={{ alignItems: isDirectInput ? 'unset' : 'center' }}>
        <p>
          문구<span style={{ color: '#FF0000' }}>*</span>
        </p>
        <div className='right_content'>
          <Select
            disabled={disabled}
            style={{ width: '100%' }}
            placeholder='문구를 선택해주세요.'
            onChange={e => {
              if (e === 'directInput') {
                setIsDirectInput(true);
                setFormData(prev => ({ ...prev, remarks: '' }));
                return;
              }
              setIsDirectInput(false);
              setFormData(prev => ({ ...prev, remarks: e }));
            }}
            options={remarksOptions}
            value={
              [
                '황망한 마음에 일일이 연락드리지 못함을 널리 혜량해 주시길 바랍니다.',
                '가시는 길 깊은 애도와 명복을 빌어주시길 진심으로 바랍니다.',
              ].includes(remarks || '')
                ? remarks
                : isDirectInput
                ? '직접입력'
                : remarks || undefined
            }
          />
          {isDirectInput && (
            <TextArea
              placeholder='문구를 입력해주세요.'
              style={{ marginTop: '16px' }}
              rows={4}
              value={remarks}
              onChange={({ target: { value } }) => {
                setFormData({ ...formData, remarks: value });
              }}
            />
          )}
        </div>
      </S.Items>
      <S.Item>
        <p>계좌</p>
        <Input
          disabled={disabled}
          className='right_content'
          placeholder='계좌(은행명, 계좌번호, 예금주명)를 입력하세요.'
          value={paymentAccount}
          onChange={({ target: { value } }) => {
            setFormData({ ...formData, paymentAccount: value });
          }}
        />
      </S.Item>
      <S.Item>
        <p>첨부파일</p>
        <div
          className='right_content'
          onClick={() => {
            if (!disabled) {
              inputFileRef.current?.click();
            }
          }}>
          <Input
            placeholder='함께 공지 원하시는 첨부파일 별첨(부고장 등)'
            disabled={disabled}
            suffix={<UploadIcon />}
            style={{ borderRadius: '16px' }}
            value={fileName}
          />
          <input ref={inputFileRef} type='file' style={{ display: 'none' }} onChange={uploadFile} />
        </div>
      </S.Item>
      <S.Item>
        <p>링크</p>
        <Input
          className='right_content'
          placeholder='링크를 입력하세요.'
          disabled={disabled}
          value={link}
          onChange={({ target: { value } }) => {
            setFormData({ ...formData, link: value });
          }}
          style={{ borderRadius: '16px' }}
        />
      </S.Item>
      <S.Item>
        <p>
          조치 종류<span style={{ color: '#FF0000' }}>*</span>
        </p>
        <Select
          placeholder='조치 종류를 선택하세요.(복수선택 가능)'
          disabled={disabled || approveStatus === 'approved'}
          mode='multiple'
          className='right_content'
          tagRender={CustomTag}
          options={actionTypeOptions}
          value={actionType}
          onChange={e => {
            setFormData({ ...formData, actionType: e });
          }}
        />
      </S.Item>
    </S.Container>
  );
};

export default FormFuneral;
